import { CheckboxField, Divider, Flex, FormFieldLayout, Text } from "@wayflyer/flyui";
import React, { useEffect, useState } from "react";

export interface Account {
  id: string;
  label: string;
  selected: boolean;
  properties?: Account[];
}

export const AccountsForm = ({
  accounts,
  setAccounts,
  selectAllLabel,
}: {
  accounts: Account[];
  setAccounts;
  selectAllLabel: string;
}) => {
  const isEveryAccountSelected = accounts.every((account) =>
    account.properties ? account.properties?.every((prop) => prop.selected) : account.selected
  );

  const isNoAccountSelected = accounts.every((account) =>
    account.properties ? account.properties?.every((prop) => !prop.selected) : !account.selected
  );

  const hasAccountsWithMultipleProperties = accounts.some((ac) => ac.properties && ac.properties.length > 1);

  const [isAllChecked, setIsAllChecked] = useState(isEveryAccountSelected);
  const updateSelected = (account) => setAccounts([...new Set([...accounts, account])]);

  const selectAll = (selected) => {
    const newAccounts = accounts.map((ac) => {
      let accountWithProperties;
      if (ac.properties) {
        accountWithProperties = ac.properties.map((prop) => (prop.selected = selected));
      }
      return ac.properties ? { ...ac, accountWithProperties } : { ...ac, selected };
    });
    setAccounts([...new Set([...newAccounts])]);
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
    setIsAllChecked(isEveryAccountSelected);
  }, [isEveryAccountSelected]);

  return (
    <Flex direction="column" gap="8">
      {(accounts.length > 1 || hasAccountsWithMultipleProperties) && (
        <>
          <CheckboxField
            name="account-property-all-field"
            options={{
              name: "account-property-all",
              label: selectAllLabel,
              defaultChecked: isEveryAccountSelected,
              indeterminate: !isEveryAccountSelected && !isNoAccountSelected,
              checked: isAllChecked,
              onChange: (checked) => {
                setIsAllChecked(checked);
                selectAll(checked);
              },
            }}
          />
          <Divider />
        </>
      )}
      <FormFieldLayout>
        <Flex direction="column" gap="6">
          {accounts.map((ac, idx) => {
            if (!Array.isArray(ac.properties)) {
              return (
                <CheckboxField
                  name={`account-property-${idx}-field`}
                  key={idx}
                  options={{
                    name: `account-property-${idx}`,
                    label: ac.label,
                    defaultChecked: !!ac.selected,
                    checked: ac.selected,
                    onChange: (checked) => {
                      ac.selected = checked;
                      updateSelected(ac);
                    },
                  }}
                />
              );
            } else {
              return (
                <React.Fragment key={`${ac.label}-${idx}`}>
                  <Text>{ac.label}</Text>
                  <FormFieldLayout>
                    {ac.properties.map((pr, subIdx) => {
                      return (
                        <CheckboxField
                          key={`${idx}-${subIdx}`}
                          name={`account-property-${idx}-${subIdx}-field`}
                          options={{
                            name: `account-property-${idx}-${subIdx}`,
                            label: pr.label,
                            defaultChecked: !!pr.selected,
                            checked: ac.properties && ac.properties[subIdx].selected,
                            onChange: (checked) => {
                              if (ac.properties) {
                                ac.properties[subIdx].selected = checked;
                                updateSelected(ac);
                              }
                            },
                          }}
                        />
                      );
                    })}
                  </FormFieldLayout>
                </React.Fragment>
              );
            }
          })}
        </Flex>
      </FormFieldLayout>
    </Flex>
  );
};
